import { api } from './client';
import { ApiResponse } from '@/types';
import { config as appConfig } from '@/config';
import axios from 'axios';

// ============================================================================
// Types
// ============================================================================

export interface FileRecord {
  _id: string;
  filename: string;
  key: string;
  owner_type: 'User' | 'Organization';
  owner_id: string;
  scope: 'private' | 'project' | 'shared';
  projectId?: string;
  visibility: 'private' | 'project' | 'org' | 'public';
  publicKey?: string;
  publicUrl?: string;
  size: number;
  contentType: string;
  etag?: string;
  checksum?: string;
  storageProvider: string;
  bucketName: string;
  thumbnails?: {
    small?: string;
    medium?: string;
    large?: string;
  };
  thumbnailStatus: 'pending' | 'processing' | 'complete' | 'failed' | 'not_applicable';
  version: number;
  parentFileId?: string;
  isLatestVersion: boolean;
  encryptionType: 'none' | 'org' | 'user_protected' | 'per_file';
  encryptionKeyId?: string;
  kdfSalt?: string;
  description?: string;
  tags: string[];
  metadata: Record<string, any>;
  storageClass: 'hot' | 'warm' | 'cold';
  lastAccessedAt?: string;
  accessCount: number;
  uploadedBy: string;
  isDeleted: boolean;
  folder?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UploadUrlOptions {
  filename: string;
  contentType: string;
  size: number;
  scope?: 'private' | 'project' | 'shared';
  projectId?: string;
  visibility?: 'private' | 'project' | 'org' | 'public';
  encryptionType?: 'none' | 'org' | 'user_protected' | 'per_file';
  passphrase?: string;
  description?: string;
  tags?: string[];
  folder?: string;
}

export interface UploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  key: string;
  expiresIn: number;
  headers?: Record<string, string>;
}

export interface FileListParams {
  page?: number;
  limit?: number;
  scope?: string;
  projectId?: string;
  visibility?: string;
  contentType?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  folder?: string;
  exact?: boolean;
}

export interface StorageUsage {
  totalFiles: number;
  totalSize: number;
  quota: number;
  usagePercentage: number;
  byUser: Array<{ userId: string; username?: string; fileCount: number; totalSize: number }>;
  byProject: Array<{ projectId: string; projectName?: string; fileCount: number; totalSize: number }>;
  byContentType: Array<{ contentType: string; fileCount: number; totalSize: number }>;
  byProvider: Array<{ provider: string; fileCount: number; totalSize: number }>;
  byEncryption: Array<{ encryptionType: string; fileCount: number; totalSize: number }>;
}

export interface AdminStorageOverview {
  totalOrgs: number;
  totalFiles: number;
  totalSize: number;
  orgs: Array<{
    orgId: string;
    orgName: string;
    orgSlug: string;
    fileCount: number;
    totalSize: number;
    quota: number;
    usagePercentage: number;
    provider: string;
    status: 'ok' | 'warning' | 'critical';
  }>;
}

// ============================================================================
// File Service
// ============================================================================

export class FileService {
  /**
   * Request a presigned upload URL
   */
  static async requestUploadUrl(
    orgSlug: string,
    options: UploadUrlOptions
  ): Promise<ApiResponse<UploadUrlResponse>> {
    return api.post(`/orgs/${orgSlug}/files/upload-url`, options);
  }

  /**
   * Upload file directly to presigned URL (bypasses backend)
   */
  static async uploadToPresignedUrl(
    uploadUrl: string,
    file: File,
    contentType: string,
    headers?: Record<string, string>,
    onProgress?: (progress: number) => void
  ): Promise<{ etag?: string }> {
    // For relative URLs (local storage), resolve against backend base URL
    // since the presigned URL is relative to the backend, not the frontend dev server
    let resolvedUrl = uploadUrl;
    if (uploadUrl.startsWith('/')) {
      const backendBase = appConfig.api.baseUrl.replace(/\/api\/v1\/?$/, '');
      resolvedUrl = `${backendBase}${uploadUrl}`;
    }
    const response = await axios.put(resolvedUrl, file, {
      headers: {
        'Content-Type': contentType,
        ...headers,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });

    return { etag: response.headers?.etag };
  }

  /**
   * Confirm upload completion
   */
  static async confirmUpload(
    orgSlug: string,
    fileId: string,
    etag?: string
  ): Promise<ApiResponse<FileRecord>> {
    return api.post(`/orgs/${orgSlug}/files/confirm`, { fileId, etag });
  }

  /**
   * Direct upload (multipart, for smaller files)
   */
  static async directUpload(
    orgSlug: string,
    file: File,
    metadata: Partial<UploadUrlOptions>,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<FileRecord>> {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata.scope) formData.append('scope', metadata.scope);
    if (metadata.projectId) formData.append('projectId', metadata.projectId);
    if (metadata.visibility) formData.append('visibility', metadata.visibility);
    if (metadata.encryptionType) formData.append('encryptionType', metadata.encryptionType);
    if (metadata.passphrase) formData.append('passphrase', metadata.passphrase);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));
    if (metadata.folder !== undefined) formData.append('folder', metadata.folder);

    const response = await api.upload<FileRecord>(
      `/orgs/${orgSlug}/files/upload`,
      file,
      onProgress
    );
    return response;
  }

  /**
   * List files for an org
   */
  static async listFiles(
    orgSlug: string,
    params?: FileListParams
  ): Promise<ApiResponse<FileRecord[]>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.set(key, String(value));
        }
      });
    }
    const query = queryParams.toString();
    return api.get(`/orgs/${orgSlug}/files${query ? `?${query}` : ''}`);
  }

  /**
   * Get a single file
   */
  static async getFile(
    orgSlug: string,
    fileId: string
  ): Promise<ApiResponse<FileRecord>> {
    return api.get(`/orgs/${orgSlug}/files/${fileId}`);
  }

  /**
   * Get presigned download URL
   */
  static async getDownloadUrl(
    orgSlug: string,
    fileId: string,
    passphrase?: string
  ): Promise<ApiResponse<{ downloadUrl: string; expiresIn: number }>> {
    const params = passphrase ? `?passphrase=${encodeURIComponent(passphrase)}` : '';
    return api.get(`/orgs/${orgSlug}/files/${fileId}/download-url${params}`);
  }

  /**
   * Delete a file (soft delete)
   */
  static async deleteFile(
    orgSlug: string,
    fileId: string
  ): Promise<ApiResponse<void>> {
    return api.delete(`/orgs/${orgSlug}/files/${fileId}`);
  }

  /**
   * Update file metadata (description, tags)
   */
  static async updateFile(
    orgSlug: string,
    fileId: string,
    data: { description?: string; tags?: string[] }
  ): Promise<ApiResponse<FileRecord>> {
    return api.patch(`/orgs/${orgSlug}/files/${fileId}`, data);
  }

  /**
   * Update file visibility
   */
  static async updateVisibility(
    orgSlug: string,
    fileId: string,
    visibility: 'private' | 'project' | 'org' | 'public'
  ): Promise<ApiResponse<FileRecord>> {
    return api.patch(`/orgs/${orgSlug}/files/${fileId}/visibility`, { visibility });
  }

  /**
   * List file versions
   */
  static async listVersions(
    orgSlug: string,
    fileId: string
  ): Promise<ApiResponse<FileRecord[]>> {
    return api.get(`/orgs/${orgSlug}/files/${fileId}/versions`);
  }

  /**
   * Get org storage usage
   */
  static async getUsage(
    orgSlug: string
  ): Promise<ApiResponse<StorageUsage>> {
    return api.get(`/orgs/${orgSlug}/files/usage`);
  }

  /**
   * Get org storage analytics
   */
  static async getAnalytics(
    orgSlug: string
  ): Promise<ApiResponse<StorageUsage>> {
    return api.get(`/orgs/${orgSlug}/storage/analytics`);
  }

  /**
   * Get admin storage overview (platform admin only)
   */
  static async getAdminOverview(): Promise<ApiResponse<AdminStorageOverview>> {
    return api.get('/admin/storage/overview');
  }

  // ============================================================================
  // Folder Management
  // ============================================================================

  static async listFolders(
    orgSlug: string,
    params?: { parent?: string; projectId?: string }
  ): Promise<ApiResponse<Array<{ name: string; path: string }>>> {
    const queryParams = new URLSearchParams();
    if (params?.parent) queryParams.set('parent', params.parent);
    if (params?.projectId) queryParams.set('projectId', params.projectId);
    const query = queryParams.toString();
    return api.get(`/orgs/${orgSlug}/folders${query ? `?${query}` : ''}`);
  }

  static async createFolder(
    orgSlug: string,
    data: { name: string; parent?: string; projectId?: string }
  ): Promise<ApiResponse<{ name: string; path: string }>> {
    return api.post(`/orgs/${orgSlug}/folders`, data);
  }

  static async renameFolder(
    orgSlug: string,
    data: { path: string; newName: string; projectId?: string }
  ): Promise<ApiResponse<{ name: string; path: string }>> {
    return api.patch(`/orgs/${orgSlug}/folders`, data);
  }

  static async deleteFolder(
    orgSlug: string,
    data: { path: string; projectId?: string }
  ): Promise<ApiResponse<void>> {
    return api.delete(`/orgs/${orgSlug}/folders`, { data });
  }

  // ============================================================================
  // Org OneDrive Shares
  // ============================================================================

  /**
   * List OneDrive shares available to the current org
   */
  static async listOrgOneDriveShares(
    orgSlug: string
  ): Promise<ApiResponse<OrgOneDriveShare[]>> {
    return api.get(`/orgs/${orgSlug}/onedrive-shares`);
  }

  /**
   * Browse files in a shared OneDrive directory
   */
  static async browseSharedOneDrive(
    orgSlug: string,
    shareId: string,
    path: string
  ): Promise<ApiResponse<OneDriveFileItem[]>> {
    return api.get(
      `/orgs/${orgSlug}/onedrive-shares/${shareId}/browse?path=${encodeURIComponent(path)}`
    );
  }

  /**
   * Get download URL for a file in a shared OneDrive directory
   */
  static async getSharedOneDriveDownloadUrl(
    orgSlug: string,
    shareId: string,
    filePath: string
  ): Promise<ApiResponse<{ downloadUrl: string }>> {
    return api.get(
      `/orgs/${orgSlug}/onedrive-shares/${shareId}/download?path=${encodeURIComponent(filePath)}`
    );
  }

  // ============================================================================
  // Personal OneDrive File Management
  // ============================================================================

  static async browsePersonalOneDrive(
    label: string,
    path: string
  ): Promise<ApiResponse<OneDriveFileItem[]>> {
    return api.get(
      `/storage-connections/onedrive/${encodeURIComponent(label)}/files?path=${encodeURIComponent(path)}`
    );
  }

  static async uploadToPersonalOneDrive(
    label: string,
    file: File,
    path: string,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<any>> {
    return api.upload(
      `/storage-connections/onedrive/${encodeURIComponent(label)}/files?path=${encodeURIComponent(path)}`,
      file,
      onProgress
    );
  }

  static async deleteFromPersonalOneDrive(
    label: string,
    path: string
  ): Promise<ApiResponse<void>> {
    return api.delete(
      `/storage-connections/onedrive/${encodeURIComponent(label)}/files?path=${encodeURIComponent(path)}`
    );
  }

  static async getPersonalOneDriveDownloadUrl(
    label: string,
    path: string
  ): Promise<ApiResponse<{ downloadUrl: string }>> {
    return api.get(
      `/storage-connections/onedrive/${encodeURIComponent(label)}/files/download?path=${encodeURIComponent(path)}`
    );
  }
}

// ============================================================================
// Org OneDrive Share Types
// ============================================================================

export interface OrgOneDriveShare {
  shareId: string;
  ownerName: string;
  label: string;
  sharedPaths: string[];
  permissions: 'read' | 'read-write';
  sharedAt?: string;
}

export interface OneDriveFileItem {
  name: string;
  path: string;
  size: number;
  lastModified: string;
  mimeType: string;
  isFolder: boolean;
}
